import { type ReflagProps, ReflagProvider, useClient } from '@reflag/react-sdk';
import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import clsx from 'clsx';
import * as React from 'react';
import { atom, RecoilRoot, useRecoilValue } from 'recoil';

import { withReflag } from '@gitbook/adaptive';
import type { User } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { isGitBookStaff } from '@gitbook/app-database';
import type { EnabledFeatureFlagsMap } from '@gitbook/app-featureflags';
import type { NavigationScreenOrgSettings, NavigationScreenState } from '@gitbook/app-navigation';
import { checkIsEnterpriseProductKey } from '@gitbook/app-pricing';
import { env, webConfig } from '@gitbook/env';
import { IconsProvider } from '@gitbook/icons';
import { UIKitProvider } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ConfirmProvider,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
    GitBookLoader,
    ImageProvider,
    LoadingMessage,
    RouterProvider,
    Toaster,
} from '@gitbook/spine-react';
import { SentryTags } from '@gitbook/util-debug';

import { AppLayout, OfflineOverlay } from './AppLayout';
import {
    AgentProvider,
    APIErrorBoundary,
    AppSearch,
    AppVersionToast,
    DndProvider,
    ErrorScreen,
    enableSentryReplay,
    HiveAppProvider,
    ImportRunToasts,
    MediaGallery,
    NotificationsProvider,
    OrganizationAccessFallback,
    OrganizationAccessPlaceholder,
    OrganizationNotFoundEmptyState,
    setSentryContext,
    UpgradeDialogProvider,
    useHideSplashscreen,
    useSetSentryTag,
} from './Components';
import { IconSourcesProvider, useInlineIconSources } from './Components/Icons';
import {
    HistoryProvider,
    type NavigateOptions,
    NavigationProvider,
    NavigationRedirect,
    NavigationScreenRenderer,
    type NavigationTarget,
    useNavigationFunctions,
    useNavigationState,
    useNeighborPreload,
} from './navigation';
import {
    AgentsScreen,
    ChangeRequestsScreen,
    ChangeSitePlanDialog,
    CollectionScreen,
    EnforceSSOWrapper,
    getScreenNeighbors,
    HomeScreen,
    IntegrationScreen,
    IntegrationsScreen,
    InvitesOrganizationScreen,
    JoinOrganizationScreen,
    LoginScreenWrapper,
    OAuthAuthorizeScreen,
    OnboardingFlow,
    OpenAPISpecScreen,
    OpenAPISpecsHomeScreen,
    OrganizationContentScreen,
    OrganizationMigrateSettings,
    RedirectOrgWithBasename,
    RedirectWithSitePreview,
    RootRedirect,
    SettingsScreen,
    SiteInviteScreen,
    SiteScreen,
    SitesListScreen,
    SiteUpgradeModal,
    SpaceScreen,
    StyleguidesScreen,
    TranslationsScreen,
    TrashScreen,
    TriageScreen,
} from './Screens';
import { TriageProcessingToasts } from './Screens/Triage/TriageContext';
import { ContentSidebar, SettingsSidebar } from './Sidebars';
import {
    apiClient,
    apiQuery,
    apiQueryClient,
    apiQueryPersistOptions,
    autoStartLogSession,
    CurrentOrganizationProvider,
    CurrentUserProvider,
    FirebaseAuthProvider,
    siteCentricOptInAtom,
    UserDataProvider,
    useCurrentOrganizationId,
    useCustomerIoInAppMessages,
    useLoggedInUser,
    usePersistOnboardingParamsFromURL,
    useUpdateOrgMemberLastSeenAt,
} from './state';
import { getFeatureFlagTestOverrides } from './testing/featureflags';
import { getImageResizedURL, useDevelopmentLiveReload } from './utils';

autoStartLogSession();

function UIKitProviderWithPaletteMenuFeature(props: { children: React.ReactNode }) {
    return <UIKitProvider>{props.children}</UIKitProvider>;
}

/**
 * Entry point component.
 */
export function Application() {
    // Capture and persist Onboarding params from the URL, if present
    usePersistOnboardingParamsFromURL();

    // Worst case scenario, we always hide the splashscreen after 3 seconds
    // to ensure it doesn't get stuck on an unhandled route
    useHideSplashscreen(4000, true);
    useDevelopmentLiveReload();
    const iconSourceAssets = React.useMemo(
        () => ({
            assetsURL: 'https://ka-p.fontawesome.com/releases/v7.2.0/',
            assetsURLToken: webConfig.icons.token,
            assetsByStyles: {
                'custom-icons': {
                    assetsURL: '/public/icons/',
                },
            },
        }),
        []
    );
    const { iconSources, loadIconSources } = useInlineIconSources(iconSourceAssets);

    return (
        <ImageProvider getResizedURL={getImageResizedURL}>
            <PersistQueryClientProvider
                client={apiQueryClient}
                persistOptions={apiQueryPersistOptions}
            >
                <RecoilRoot>
                    <ErrorScreen>
                        <IconsProvider
                            assetsURL={iconSourceAssets.assetsURL}
                            assetsURLToken={iconSourceAssets.assetsURLToken}
                            assetsByStyles={iconSourceAssets.assetsByStyles}
                            iconSources={iconSources}
                        >
                            <IconSourcesProvider load={loadIconSources}>
                                <ConfirmProvider>
                                    <UIKitProviderWithPaletteMenuFeature>
                                        <DndProvider>
                                            <FirebaseAuthProvider>
                                                <CurrentUserProvider>
                                                    <HiveAppProvider>
                                                        <React.Suspense
                                                            fallback={<FullScreenLoadingFallback />}
                                                        >
                                                            <HistoryProvider>
                                                                <NavigationProvider persist>
                                                                    <ScreenNavigationRouterProvider>
                                                                        <LoginScreenWrapper>
                                                                            <UserDataProvider>
                                                                                <ApplicationWithOrganization>
                                                                                    <AgentProvider>
                                                                                        <MainApplication />
                                                                                    </AgentProvider>
                                                                                </ApplicationWithOrganization>
                                                                            </UserDataProvider>
                                                                        </LoginScreenWrapper>
                                                                        <Toaster />
                                                                    </ScreenNavigationRouterProvider>
                                                                </NavigationProvider>
                                                            </HistoryProvider>
                                                            <OfflineOverlay />
                                                        </React.Suspense>
                                                    </HiveAppProvider>
                                                </CurrentUserProvider>
                                            </FirebaseAuthProvider>
                                        </DndProvider>
                                    </UIKitProviderWithPaletteMenuFeature>
                                </ConfirmProvider>
                            </IconSourcesProvider>
                        </IconsProvider>
                    </ErrorScreen>
                </RecoilRoot>
            </PersistQueryClientProvider>
        </ImageProvider>
    );
}

export type Href = NavigationTarget | string;

declare module '@gitbook/spine-react' {
    interface RouterConfig {
        href: Href;
        routerOptions: NavigateOptions;
    }
}

/**
 * Provider to navigate to a screen when a link is clicked in Spine components.
 */
function ScreenNavigationRouterProvider(props: { children: React.ReactNode }) {
    const { navigate, getNavigationURL } = useNavigationFunctions();
    return (
        <RouterProvider
            navigate={(href, options) => {
                if (typeof href === 'string') {
                    window.location.href = href;
                } else {
                    navigate(href, options);
                }
            }}
            useHref={(href: Href) => {
                if (typeof href === 'string') {
                    return href;
                }
                return getNavigationURL(href);
            }}
        >
            {props.children}
        </RouterProvider>
    );
}

/**
 * Check if the focus is in an editable element.
 */
function isFocusInEditable(): boolean {
    const { activeElement } = document;

    if (!activeElement || !(activeElement instanceof HTMLElement)) {
        return false;
    }

    if (activeElement.isContentEditable) {
        return true;
    }

    if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
        return !activeElement.readOnly && !activeElement.disabled;
    }

    if (activeElement instanceof HTMLSelectElement) {
        return !activeElement.disabled;
    }

    return false;
}

const autoFeedbackHandler: NonNullable<ReflagProps['feedback']>['autoFeedbackHandler'] = (
    message,
    handlers
) => {
    if (!isFocusInEditable()) {
        handlers.openFeedbackForm({
            title: message.question,
        });
    }
};

/**
 * Application component being rendered when an user is logged in.
 */
const MainApplication = React.memo(function MainApplication() {
    useCustomerIoInAppMessages();
    const currentUser = useLoggedInUser();
    const { screen } = useNavigationState();
    const siteCentricOptIn = useRecoilValue(siteCentricOptInAtom);
    // Match the UTC signup date stored through the server-side analytics identify flow.
    const userSignUpDate = (() => {
        const creationTime = currentUser.metadata.creationTime;
        if (!creationTime) {
            return undefined;
        }

        const createdAt = new Date(creationTime);
        if (Number.isNaN(createdAt.getTime())) {
            return undefined;
        }

        return createdAt.toISOString().slice(0, 10);
    })();

    return (
        <ReflagProvider
            publishableKey={webConfig.bucketCo.publishableKey}
            fallbackFlags={getFeatureFlagTestOverrides()}
            context={{
                user: {
                    id: currentUser.uid,
                    email: currentUser.email ?? undefined,
                    name: currentUser.displayName ?? '',
                    userSignUpDate,
                    ...(siteCentricOptIn ? { siteCentricOptIn } : {}),
                },
                company:
                    'organization' in screen && screen.organization
                        ? {
                              id: screen.organization,
                          }
                        : undefined,
            }}
            toolbar={
                (isGitBookStaff(currentUser) || env === 'development') &&
                !process.env.GITBOOK_PREDICTABLE_UI
            }
            feedback={{ autoFeedbackHandler }}
        >
            <TrackFeatureFlags />
            <AppReloadingOverlay />
            <NotificationsProvider>
                <div className="flex min-h-0 flex-1 flex-col bg-side">
                    <NavigationScreenRenderer
                        Screen={ScreenRouter}
                        fallback={<FullScreenLoadingFallback />}
                    />
                    <AppSearch />
                </div>
                <MediaGallery />
                <SiteUpgradeModal />
                <ChangeSitePlanDialog />
                <ImportRunToasts />
                <TriageProcessingToasts />
                <AppVersionToast />
            </NotificationsProvider>
        </ReflagProvider>
    );
});

function ApplicationWithOrganization({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const { screen } = useNavigationState();
    const currentOrgId =
        screen.type !== 'org_join' && 'organization' in screen
            ? (screen.organization ?? null)
            : null;

    // `getOrganizationById` returns 404 when the org doesn't exist or the user has no permission
    // to see it. The boundary surfaces the [[OrganizationNotFoundEmptyState]] in both cases. We
    // key the boundary by `currentOrgId` so navigating to a different org resets it.
    return (
        <APIErrorBoundary
            fallback={{ 404: <OrganizationNotFoundEmptyState /> }}
            resetKeys={[currentOrgId]}
        >
            <ApplicationWithOrganizationInner currentOrgId={currentOrgId}>
                {children}
            </ApplicationWithOrganizationInner>
        </APIErrorBoundary>
    );
}

function ApplicationWithOrganizationInner({
    children,
    currentOrgId,
}: {
    children: React.ReactNode;
    currentOrgId: string | null;
}): React.ReactElement {
    const [
        { data: organization },
        {
            data: { items: userOrganizations },
        },
        { data: authenticatedUser },
    ] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getOrganizationById',
                currentOrgId ? { organizationId: currentOrgId } : skipToken
            ),
            apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }),
            // We don't care about the data, but the query will ensure the DB profile
            // for the authenticated user and its permissions are created after a signup
            apiQuery('getAuthenticatedUser', {}),
        ],
    });

    const userIsMemberOfCurrentOrg = currentOrgId
        ? userOrganizations.some((org) => org.id === currentOrgId)
        : false;
    const userCannotAccessCurrentOrg = Boolean(currentOrgId && !userIsMemberOfCurrentOrg);

    // Track the organization context in Sentry
    React.useEffect(
        () =>
            setSentryContext({
                organization:
                    organization && !userCannotAccessCurrentOrg
                        ? {
                              id: organization.id,
                              title: organization.title,
                              type: organization.type,
                              plan: organization.plan,
                          }
                        : null,
            }),
        [organization, userCannotAccessCurrentOrg]
    );

    useSetSentryTag(
        SentryTags.ORGANIZATION_PLAN,
        userCannotAccessCurrentOrg || !organization ? null : organization.plan
    );

    // Record session in Sentry replay for enterprise customers
    const organizationPlan = organization?.plan;
    React.useEffect(() => {
        if (
            organizationPlan &&
            checkIsEnterpriseProductKey(organizationPlan) &&
            !userCannotAccessCurrentOrg
        ) {
            enableSentryReplay();
        }
    }, [organizationPlan, userCannotAccessCurrentOrg]);

    if (userCannotAccessCurrentOrg) {
        return <OrganizationAccessFallback />;
    }

    return (
        <CurrentOrganizationProvider organization={organization ?? null}>
            <SyncUserProfile authenticatedUser={authenticatedUser} />
            <EnforceSSOWrapper organization={organization}>{children}</EnforceSSOWrapper>
        </CurrentOrganizationProvider>
    );
}

/**
 * Sync the API user profile with the Firebase user.
 * When a user changes its email they are unsynced.
 * It should be handled server-side with webhooks but not sure it's possible.
 */
function SyncUserProfile(props: { authenticatedUser: User }) {
    const { authenticatedUser } = props;
    const user = useLoggedInUser();

    React.useEffect(() => {
        if (user.email && authenticatedUser.email && user.email !== authenticatedUser.email) {
            apiClient.users.syncProfileWithAuth().catch(() => {
                // It's not critical if it fails,
                // it will be retry when the user reloads.
            });
        }
    }, [user.email, authenticatedUser.email]);

    return null;
}

/**
 * Render any screen, overlay or main ones.
 */
function ScreenRouter(props: {
    screen: NavigationScreenState;
    isOverlay: boolean;
}): React.ReactElement {
    const { screen, isOverlay } = props;

    // Fallback to start hiding the splashscreen
    // Deeper components in the tree will hide without a delay when they are ready
    useHideSplashscreen();
    useUpdateOrgMemberLastSeenAt();

    // Warm the chunks of screens the user is likely to visit next, once this
    // screen has settled and the browser is idle.
    useNeighborPreload(getScreenNeighbors(screen.type));

    const organizationId = useCurrentOrganizationId();

    switch (screen.type) {
        case 'oauth_authorize':
            return <OAuthAuthorizeScreen screen={screen} />;
        case 'root':
            return <RootRedirect screen={screen} />;
        case 'org_settings':
            return <OrgSettingsScreenRouter screen={screen} />;
        case 'account':
            return (
                <AppLayout sidebar={<SettingsSidebar screen={screen} />} screen={screen}>
                    <SettingsScreen screen={screen} />
                </AppLayout>
            );
        case 'org_join':
            return <JoinOrganizationScreen screen={screen} />;
        case 'org_invite':
            return <InvitesOrganizationScreen screen={screen} isOverlay={isOverlay} />;
        case 'site_invite':
            return <SiteInviteScreen screen={screen} isOverlay={isOverlay} />;
        case 'welcome':
            return <OnboardingFlow screen={screen} />;
        case 'org_with_basename':
            return <RedirectOrgWithBasename screen={screen} />;
        case 'site_preview_auth':
            return <RedirectWithSitePreview screen={screen} />;
        case 'join':
            // If there is a user authenticated, but they authenticated using the join screen,
            // redirect to the welcome page so users can get started with setting up an org.
            return <NavigationRedirect to={{ screen: { type: 'root' } }} replace />;
        default:
            return (
                <AppLayout sidebar={<ContentSidebar screen={screen} />} screen={screen}>
                    {organizationId ? (
                        <OrganizationAccessPlaceholder organization={organizationId}>
                            <UpgradeDialogProvider>
                                <MainScreenSwitch screen={screen} />
                            </UpgradeDialogProvider>
                        </OrganizationAccessPlaceholder>
                    ) : (
                        <MainScreenSwitch screen={screen} />
                    )}
                </AppLayout>
            );
    }
}

/**
 * Render an organization settings screen.
 */
export function OrgSettingsScreenRouter(props: { screen: NavigationScreenOrgSettings }) {
    const { screen } = props;
    const { tab, ...rest } = screen;

    if (tab.type === 'upgrade') {
        const migrateScreen = { ...rest, tab };
        return <OrganizationMigrateSettings screen={migrateScreen} />;
    }

    const settingsScreen = { ...rest, tab };
    return (
        <AppLayout sidebar={<SettingsSidebar screen={screen} />} screen={screen}>
            <SettingsScreen screen={settingsScreen} />
        </AppLayout>
    );
}

/**
 * Render a navigation screen inside the main UI with the sidebar.
 *
 * If you add or move a lazy screen in this switch, update
 * `Screens/routePreload.ts` so boot-time preloading stays in sync — drift
 * only costs the optimization, never correctness.
 */
function MainScreenSwitch(props: { screen: NavigationScreenState }): React.ReactElement | null {
    const { screen } = props;

    switch (screen.type) {
        case 'home':
            return <HomeScreen screen={screen} />;
        case 'site':
            return <SiteScreen screen={screen} />;
        case 'sites':
            return <SitesListScreen screen={screen} />;
        case 'space':
            return <SpaceScreen screen={screen} />;
        case 'collection':
            return <CollectionScreen screen={screen} />;
        case 'trash':
            return <TrashScreen screen={screen} />;
        case 'org_content':
            return <OrganizationContentScreen screen={screen} />;
        case 'styleguides':
            return <StyleguidesScreen screen={screen} />;
        case 'integration':
            return <IntegrationScreen screen={screen} />;
        case 'integrations':
            return <IntegrationsScreen screen={screen} />;
        case 'openapi_specs':
            return <OpenAPISpecsHomeScreen screen={screen} />;
        case 'openapi_spec':
            return <OpenAPISpecScreen screen={screen} />;
        case 'agents':
            return <AgentsScreen screen={screen} />;
        case 'change_requests':
            return <ChangeRequestsScreen screen={screen} />;
        case 'triage':
            return <TriageScreen screen={screen} />;
        case 'translations':
            return <TranslationsScreen screen={screen} />;
        default:
            return <MainScreenFallback />;
    }
}

function MainScreenFallback() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Page />
            </EmptyStateIcon>
            <EmptyStateNotFoundHeading />
            <Description>The page you are looking for does not exist.</Description>
            <EmptyStateActions>
                <Button href={{ screen: { type: 'home' } }} label="Back to home" kind="secondary" />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Overlay that shows the full-screen loader when the app is about to reload.
 */
function AppReloadingOverlay() {
    const reloadingMessage = useRecoilValue(appReloadingMessageAtom);
    if (!reloadingMessage) {
        return null;
    }
    return (
        <FullScreenLoadingFallback className="z-[10000]">
            <LoadingMessage message={reloadingMessage} />
        </FullScreenLoadingFallback>
    );
}
/**
 * Atom to signal the app is reloading and define a message to display on the full-screen loader.
 */
export const appReloadingMessageAtom = atom<string | null>({
    key: 'appReloadingMessageAtom',
    default: null,
});

/**
 * A loading fallback that covers the full screen.
 */
function FullScreenLoadingFallback(props: { children?: React.ReactNode; className?: string }) {
    const { children, className } = props;
    return (
        <div
            className={clsx(
                'fixed inset-0 z-10 flex size-full flex-col items-center justify-center bg-base',
                className
            )}
            data-testid="app.fullScreenLoadingFallback"
        >
            <GitBookLoader />
            {children}
        </div>
    );
}

/**
 * Track feature flags changes in Amplitude.
 */
function TrackFeatureFlags() {
    const client = useClient();

    React.useEffect(() => {
        if (!client) {
            return;
        }

        // Identify the user when the feature flags are updated
        client.on('featuresUpdated', () => {
            const rawFlags = client.getFlags();
            const flags = Object.fromEntries(
                Object.entries(rawFlags).map(([key, value]) => [key, value?.isEnabled])
            ) as EnabledFeatureFlagsMap;
            analytics.identify({ flags });
        });
    }, [client]);

    React.useEffect(() => {
        if (!client) {
            return;
        }

        return withReflag(client);
    }, [client]);

    return null;
}
